import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { AssignEntryBulkDto } from "../../../model/entry/dto/AssignEntryBulkDto";
import { PageMeta } from "../../../model/meta/PageMeta";
import { entryActions } from "../../../redux/entry/entry.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { EntityAssignCheckbox } from "../../../components/EntityAssignCheckbox";
import { useDrawersContext } from "../../tasks/drawers-context";

const Container = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${Spacing.m};
  width: 100%;
`;

export const EntryAssignBulkModal: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [selectedMembersIds, setSelectedMembersIds] = useState<string[]>([]);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    entriesToAssign,
    entryAssignBulkVisible,
    setEntryAssignBulkVisible,
    setSelectedRows,
  } = useDrawersContext();

  const loggedInUserId = useAppSelector(
    userSliceSelectors.selectLoggedInUserId,
  )!;

  const onAssign = async () => {
    if (!entriesToAssign.length) {
      return;
    }

    setLoading(true);

    try {
      await dispatch(
        entryActions.assignEntryBulk({
          assigneeIds: selectedMembersIds,
          entriesIds: entriesToAssign,
          userId: loggedInUserId,
        } as AssignEntryBulkDto),
      ).unwrap();
      await dispatch(
        entryActions.getLatestEntries({
          userId: loggedInUserId,
          meta: PageMeta.create(),
        }),
      ).unwrap();
      AtiraToast.success(t("entries.table.assign-bulk-modal.success"));
      localOnClose();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  const localOnClose = () => {
    setSelectedMembersIds([]);
    setEntryAssignBulkVisible(false);
    setSelectedRows([]);
  };

  if (!entryAssignBulkVisible) {
    return null;
  }

  return (
    <Fragment>
      <Modal
        open={entryAssignBulkVisible}
        onClose={localOnClose}
        content={{ height: "25rem", maxHeight: "25rem", overflow: "scroll" }}
        title={t("entries.table.assign-bulk-modal.header", {
          count: entriesToAssign.length,
        })}
      >
        <Container>
          <Flex flexDirection="column" gap="m">
            <Text>{t("common.assign.modal.info")}</Text>
            <Info info={t("entries.table.assign-bulk-modal.note")} />

            <Text>{t("common.assign.modal.description")}</Text>

            <EntityAssignCheckbox
              value={selectedMembersIds}
              onChange={(v: string[]) => setSelectedMembersIds(v)}
            />

            <Button
              disabled={loading}
              loading={loading}
              onClick={onAssign}
              margin="none"
              hover
            >
              {t("common.assign")}
            </Button>
          </Flex>
        </Container>
      </Modal>
    </Fragment>
  );
};
